import api from './api';

// Get User Profile
export const getProfile = async () => {
    try {
        const response = await api.get('/profile');
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Save/Update User Profile
export const updateProfile = async (profileData) => {
    try {
        const response = await api.put('/profile', profileData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Upload File
export const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
        // Axios sets Content-Type to multipart/form-data automaticall if body is FormData
        const response = await api.post('/files/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// --- Mapping Functions ---

/**
 * Maps API response to form state
 */
export const mapApiToFormState = (apiData) => {
    const personal = apiData.personal || {};
    const addressCurrent = apiData.address?.current || {};
    const addressPermanent = apiData.address?.permanent || {};
    const academic = apiData.academic || {};
    const bank = apiData.bank || {};
    const portal = apiData.portal || {};
    const salary = apiData.salary || {};
    const documents = apiData.documents || {};
    const achievement = apiData.achievement || {};

    return {
        formData: {
            // Personal
            fullName: personal.full_name || [personal.first_name, personal.middle_name, personal.last_name].filter(Boolean).join(' '),
            dateOfBirth: personal.date_of_birth || '',
            phoneNumber: personal.phone_number || '',
            altPhoneNumber: personal.alt_phone_number || '',
            whatsappNumber: personal.whatsapp_number || '',
            email: personal.email || '',
            altEmail: personal.alt_email || '',

            // Current Address
            currentAddressLine1: addressCurrent.line_1 || '',
            currentAddressLine2: addressCurrent.line_2 || '',
            currentCity: addressCurrent.city || '',
            currentState: addressCurrent.state || '',
            currentPinCode: addressCurrent.pincode || '',
            currentCountry: addressCurrent.country || '',

            // Permanent Address
            permanentAddressLine1: addressPermanent.line_1 || '',
            permanentAddressLine2: addressPermanent.line_2 || '',
            permanentCity: addressPermanent.city || '',
            permanentState: addressPermanent.state || '',
            permanentPinCode: addressPermanent.pincode || '',
            permanentCountry: addressPermanent.country || '',

            // Academic (Common for both if returned in academic object)
            university: academic.university || '',
            collegeName: academic.college_name || '',
            subject: academic.subject_id || '',
            stream: academic.stream_id || '',
            specialization: academic.specialization_id || '',
            presentResearchArea: academic.current_research_area || '',

            // Academic (Student Only)
            session: academic.session || '',
            startingYear: academic.starting_year || '',
            expectedCompletionYear: academic.completion_year || '',

            // Academic (Professor Only)
            facultyDepartment: academic.faculty_department || '',
            designation: academic.designation || '',
            dateOfJoining: academic.date_of_joining || '',
            retirementDate: academic.retirement_date || '',

            // Achievement (Professor)
            awardCertificate: achievement.title || '',
            awardYear: achievement.year || '',
            awardedBy: achievement.awarded_by || '',
            achievementNote: achievement.note || '',

            // Portal (User/Admin)
            portalDesignation: portal.portal_designation || '',
            portalJoiningDate: portal.portal_joining_date || '',
            lastCompany: portal.last_company || '',
            totalExperience: portal.total_experience || '',

            // Bank
            reviewConsultancyCharges: bank.review_consultancy_charges || '',
            bankName: bank.bank_name || '',
            ifscCode: bank.ifsc_code || '',
            accountNumber: bank.account_number || '',
            branch: bank.branch || '',
            upiId: bank.upi_id || '',
            phonePayNumber: bank.phonepe_number || '',
            gPayNumber: bank.gpay_number || '',

            // Salary
            basicSalary: salary.basic_salary || '',
            hra: salary.hra || '',
            da: salary.da || '',
            perksFacilities: salary.perks || '',
            ctc: salary.ctc || '',
            deduction: salary.deductions || '',
            pf: salary.pf || '',
            medical: salary.medical || '',
            advancedItDeduction: salary.advanced_it || '',

            // Documents
            aadhaarNumber: documents.aadhaar_number || '',
        },
        profileImageUrl: documents.profile_image_url || academic.profile_image_url || null,
        aadhaarFrontUrl: documents.aadhaar_front_url || null,
        aadhaarBackUrl: documents.aadhaar_back_url || null,
        educationHistory: apiData.education_history || [],
        selectedPortalSubjects: academic.preferred_subject_ids || [],
        selectedPortalStream: academic.preferred_stream_ids || [],
        selectedPortalSpecializations: academic.preferred_specialization_ids || [],
        isRetired: academic.is_retired || false,
    };
};

/**
 * Maps form state to API request body
 */
export const mapFormStateToApi = (formData, userRole, educationEntries, selectedPortalSubjects, selectedPortalStream, selectedPortalSpecializations, documentUrls = {}) => {
    const studentRoles = ['graduate', 'post-graduate', 'phd', 'student'];
    const academicRoles = ['professor', 'reviewer', 'editorial_board', 'researcher'];

    const isStudent = studentRoles.includes(userRole);
    const isProfessor = academicRoles.includes(userRole); // Include all academic roles
    const isUserOrAdmin = userRole === 'user' || userRole === 'admin';

    const payload = {
        personal: {
            full_name: formData.fullName,
            date_of_birth: formData.dateOfBirth,
            phone_number: formData.phoneNumber,
            alt_phone_number: formData.altPhoneNumber,
            whatsapp_number: formData.whatsappNumber,
            email: formData.email,
            alt_email: formData.altEmail,
        },
        address: {
            current: {
                line_1: formData.currentAddressLine1,
                line_2: formData.currentAddressLine2,
                city: formData.currentCity,
                state: formData.currentState,
                pincode: formData.currentPinCode,
                country: formData.currentCountry,
            },
            permanent: {
                line_1: formData.permanentAddressLine1,
                line_2: formData.permanentAddressLine2,
                city: formData.permanentCity,
                state: formData.permanentState,
                pincode: formData.permanentPinCode,
                country: formData.permanentCountry,
            },
        },
        documents: {
            profile_image_url: documentUrls.profileImageUrl || '',
            aadhaar_number: formData.aadhaarNumber,
            aadhaar_front_url: documentUrls.aadhaarFrontUrl || '',
            aadhaar_back_url: documentUrls.aadhaarBackUrl || '',
        },
    };

    // Academic section based on role
    if (isStudent) {
        payload.academic = {
            type: 'student',
            university: formData.university,
            college_name: formData.collegeName,
            subject_id: formData.subject || null,
            stream_id: formData.stream || null,
            specialization_id: formData.specialization || null,
            session: formData.session,
            starting_year: formData.startingYear ? parseInt(formData.startingYear) : null,
            completion_year: formData.expectedCompletionYear ? parseInt(formData.expectedCompletionYear) : null,
            current_research_area: formData.presentResearchArea || '',
            profile_image_url: documentUrls.profileImageUrl || '',
        };
    } else if (isProfessor) {
        payload.academic = {
            type: 'professor',
            university: formData.university,
            college_name: formData.collegeName,
            faculty_department: formData.facultyDepartment,
            designation: formData.designation,
            date_of_joining: formData.dateOfJoining || null,
            is_retired: !!formData.retirementDate,
            retirement_date: formData.retirementDate || null,
            preferred_subject_ids: selectedPortalSubjects,
            preferred_stream_ids: selectedPortalStream,
            preferred_specialization_ids: selectedPortalSpecializations,
            subject_id: formData.subject || null,
            stream_id: formData.stream || null,
            specialization_id: formData.specialization || null,
            current_research_area: formData.presentResearchArea || '',
            profile_image_url: documentUrls.profileImageUrl || '',
        };
        // Achievement for professors
        if (formData.awardCertificate || formData.awardYear) {
            payload.achievement = {
                title: formData.awardCertificate,
                year: formData.awardYear ? parseInt(formData.awardYear) : null,
                awarded_by: formData.awardedBy,
                note: formData.achievementNote,
            };
        }
    } else {
        payload.academic = { type: 'none' };
    }

    // Portal for User/Admin and Academic Roles
    if (isUserOrAdmin || isProfessor) {
        payload.portal = {
            portal_designation: formData.portalDesignation,
            portal_joining_date: formData.portalJoiningDate || null,
            last_company: formData.lastCompany,
            total_experience: formData.totalExperience,
        };
    }

    // Bank for Professor/User/Admin
    if (isProfessor || isUserOrAdmin) {
        payload.bank = {
            review_consultancy_charges: formData.reviewConsultancyCharges ? parseFloat(formData.reviewConsultancyCharges) : null,
            bank_name: formData.bankName,
            branch: formData.branch,
            account_number: formData.accountNumber,
            ifsc_code: formData.ifscCode,
            upi_id: formData.upiId,
            phonepe_number: formData.phonePayNumber,
            gpay_number: formData.gPayNumber,
        };
    }

    // Salary for Admin only
    if (userRole === 'admin') {
        payload.salary = {
            basic_salary: formData.basicSalary ? parseFloat(formData.basicSalary) : 0,
            hra: formData.hra ? parseFloat(formData.hra) : 0,
            da: formData.da ? parseFloat(formData.da) : 0,
            perks: formData.perksFacilities ? parseFloat(formData.perksFacilities) : 0,
            ctc: formData.ctc ? parseFloat(formData.ctc) : 0,
            deductions: formData.deduction ? parseFloat(formData.deduction) : 0,
            pf: formData.pf ? parseFloat(formData.pf) : 0,
            medical: formData.medical ? parseFloat(formData.medical) : 0,
            advanced_it: formData.advancedItDeduction ? parseFloat(formData.advancedItDeduction) : 0,
        };
    }

    // Education History
    if (educationEntries && educationEntries.length > 0) {
        payload.education_history = educationEntries.map(entry => ({
            qualification: entry.lastQualification,
            board_university: entry.boardUniversity,
            institute_name: entry.instituteName,
            passing_year: entry.passingYear ? parseInt(entry.passingYear) : null,
            marks_grade: entry.marksGrade,
            certificate_url: entry.certificateUrl || '',
            marksheet_url: entry.marksheetUrl || '',
        }));
    }

    return payload;
};
